import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const EmailComposer = ({ onSend, isSending = false, toEmail = '' }) => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientType, setRecipientType] = useState('accountOwner');

  const handleSend = () => {
    // Convert line breaks to HTML <br> tags for proper email rendering
    const htmlBody = body.replace(/\n/g, '<br>');
    onSend({ subject, body: htmlBody, recipientType });
  };

  const isDisabled = !subject.trim() || !body.trim() || isSending;

  const recipientOptions = [
    { value: 'accountOwner', label: t('groupEmail.composer.to.options.accountOwner') },
    { value: 'both', label: t('groupEmail.composer.to.options.both') },
    { value: 'classResponsible', label: t('groupEmail.composer.to.options.classResponsible') },
  ];

  return (
    <div className="w-full flex flex-col space-y-4">
      {/* Recipient Type Selection */}
      <div className="flex flex-col space-y-2 mb-2">
        <label className="text-gray-700 font-medium">Envoyer un e-mail à:</label>
        <div className="flex flex-wrap gap-3">
          {recipientOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setRecipientType(option.value)}
              disabled={isSending}
              className={`px-4 py-3 rounded-xl font-semibold text-xs transition-all duration-200 ${
                recipientType === option.value
                  ? 'bg-teal-30 text-blue-10'
                  : 'bg-gray-60 text-gray-50 hover:bg-teal-10'
              } ${isSending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-teal-10/80 rounded-xl overflow-hidden">
        <div className="p-4">
          {toEmail && (
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                {t('groupEmail.composer.to')}
              </label>
              <input
                type="text"
                className="w-full p-3 border border-teal-30 rounded-md bg-white text-sm placeholder:text-sm"
                value={toEmail}
                disabled={true}
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              {t('groupEmail.composer.subject')}
            </label>
            <input
              type="text"
              placeholder={t('groupEmail.composer.subjectPlaceholder')}
              className="w-full p-3 border border-teal-30 rounded-md bg-white text-sm placeholder:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-40"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSending}
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {t('groupEmail.composer.message')}
            </label>
            <textarea
              placeholder={t('groupEmail.composer.messagePlaceholder')}
              className="w-full p-4 min-h-[150px] border border-teal-30 rounded-md bg-white resize-none text-sm placeholder:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-40"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={isSending}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSend}
          disabled={isDisabled}
          className={`${
            isDisabled
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-10 hover:scale-105 active:scale-95'
          } text-white transition-all duration-300 py-2 pl-6 pr-4 rounded-xl flex items-center gap-2 font-bold`}>
          {isSending ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <>
              <span>{t('groupEmail.composer.sendButton')}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailComposer;
